import type { MdCasePiTagReference, MdCaseReferences } from './mdCase'

export const buildMdCasePiTagReferenceId = (
  piTagId: string,
  startAt: string | null,
  endAt: string | null
): string =>
  ['pi-tag-ref', piTagId, startAt ?? 'null', endAt ?? 'null'].join('::')

/**
 * Resolves the Rolodex datapoint entry id to use for APIs keyed by `entry_ids`
 * (time series, etc.), in priority order:
 *
 * 1. `datapoint_entry_id` — explicit canonical field when set.
 * 2. `id` — agent-created references store the datapoint entry id here directly
 *    (e.g. `longridge-HpStmPress`). UI-created references use the synthetic
 *    `pi-tag-ref::…` key instead, so we skip those.
 * 3. `pi_tag_id` — legacy fallback when `id` is synthetic and no explicit field exists.
 */
export const getMdCasePiTagDatapointEntryId = (
  reference: MdCasePiTagReference
): string | null => {
  const fromEntry = reference.datapoint_entry_id?.trim()
  if (fromEntry) return fromEntry

  const id = reference.id?.trim()
  if (id && !id.startsWith('pi-tag-ref::')) return id

  const legacy = reference.pi_tag_id?.trim()
  return legacy || null
}

export const deriveLegacyPiTagFromReferences = (
  piTagItems: readonly MdCasePiTagReference[]
): string | null => {
  const uniquePiTagIds = dedupeStrings(
    piTagItems.flatMap((item) => (item.pi_tag_id ? [item.pi_tag_id] : []))
  )

  return uniquePiTagIds.length === 1 ? uniquePiTagIds[0] : null
}

export const buildMdCaseReferencesValue = ({
  currentReferences,
  piTagItems,
  documents,
}: {
  currentReferences: MdCaseReferences
  piTagItems?: readonly MdCasePiTagReference[]
  documents?: readonly string[]
}): MdCaseReferences => {
  const nextPiTagItems =
    piTagItems !== undefined
      ? [...piTagItems]
      : [...(currentReferences.pi_tag_items ?? [])]
  const nextDocuments =
    documents !== undefined
      ? dedupeStrings(
          documents
            .map((document) => document.trim())
            .filter((document) => document.length > 0)
        )
      : [...currentReferences.documents]

  return {
    ...currentReferences,
    pi_tag:
      piTagItems !== undefined
        ? deriveLegacyPiTagFromReferences(nextPiTagItems)
        : currentReferences.pi_tag,
    pi_tag_items: nextPiTagItems,
    documents: nextDocuments,
  }
}

export const upsertMdCasePiTagReference = (
  currentReferences: MdCaseReferences,
  reference: MdCasePiTagReference
): MdCaseReferences => {
  const nextReference = normalizePiTagReference(reference)
  const currentPiTagItems = currentReferences.pi_tag_items ?? []
  const matchingIndex = currentPiTagItems.findIndex(
    (currentReference) => currentReference.id === nextReference.id
  )

  const nextPiTagItems =
    matchingIndex >= 0
      ? currentPiTagItems.map((currentReference, index) =>
          index === matchingIndex ? nextReference : currentReference
        )
      : [nextReference, ...currentPiTagItems]

  return buildMdCaseReferencesValue({
    currentReferences,
    piTagItems: nextPiTagItems,
  })
}

export const removeMdCasePiTagReference = (
  currentReferences: MdCaseReferences,
  referenceId: string
): MdCaseReferences =>
  buildMdCaseReferencesValue({
    currentReferences,
    piTagItems: (currentReferences.pi_tag_items ?? []).filter(
      (reference) => reference.id !== referenceId
    ),
  })

export const addMdCaseDocumentReference = (
  currentReferences: MdCaseReferences,
  document: string
): MdCaseReferences => {
  const normalizedDocument = document.trim()
  if (!normalizedDocument) {
    return currentReferences
  }

  return buildMdCaseReferencesValue({
    currentReferences,
    documents: [
      normalizedDocument,
      ...currentReferences.documents.filter(
        (currentDocument) => currentDocument.trim() !== normalizedDocument
      ),
    ],
  })
}

export const removeMdCaseDocumentReference = (
  currentReferences: MdCaseReferences,
  document: string
): MdCaseReferences => {
  const normalizedDocument = document.trim()

  return buildMdCaseReferencesValue({
    currentReferences,
    documents: currentReferences.documents.filter(
      (currentDocument) => currentDocument.trim() !== normalizedDocument
    ),
  })
}

export const isMdCaseDocumentReferenceUrl = (document: string): boolean =>
  /^(https?:\/\/|mailto:)/i.test(document.trim())

function normalizePiTagReference(
  reference: MdCasePiTagReference
): MdCasePiTagReference {
  return {
    ...reference,
    pi_tag_id: reference.pi_tag_id?.trim() || null,
    datapoint_entry_id: reference.datapoint_entry_id?.trim() || null,
    chart_id: reference.chart_id?.trim() || null,
    added_by: reference.added_by?.trim() || null,
    added_at: reference.added_at?.trim() || null,
  }
}

function dedupeStrings(values: readonly string[]): string[] {
  const dedupedValues: string[] = []
  const seenValues = new Set<string>()

  values.forEach((value) => {
    if (seenValues.has(value)) {
      return
    }

    seenValues.add(value)
    dedupedValues.push(value)
  })

  return dedupedValues
}
